import { offlineDB, STORES } from './offlineDB'
import api from './api'
import type { Task, Expense, Quotation, MealPlan, SyncOperation } from '@/types'

type SyncableResource = 'tasks' | 'expenses' | 'quotations' | 'mealplans'

class OfflineSyncService {
  private isSyncing = false
  private syncInterval: number | null = null

  startPeriodicSync(intervalMs = 30000): void {
    if (this.syncInterval) return
    
    this.syncInterval = window.setInterval(() => {
      if (navigator.onLine) {
        this.sync()
      }
    }, intervalMs)
  }

  stopPeriodicSync(): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval)
      this.syncInterval = null
    }
  }

  async queueOperation(
    type: 'create' | 'update' | 'delete',
    resource: SyncableResource,
    resourceId: string,
    data: Record<string, unknown>
  ): Promise<string> {
    const operationId = crypto.randomUUID()
    
    await offlineDB.addToSyncQueue({
      id: operationId,
      type,
      resource,
      resourceId,
      data,
      timestamp: new Date().toISOString()
    })

    await this.saveOfflineData(resource, resourceId, data)
    
    return operationId
  }

  private async saveOfflineData(
    resource: SyncableResource,
    resourceId: string,
    data: Record<string, unknown>
  ): Promise<void> {
    const storeMap: Record<SyncableResource, string> = {
      tasks: STORES.TASKS,
      expenses: STORES.EXPENSES,
      quotations: STORES.QUOTATIONS,
      mealplans: STORES.MEALPLANS
    }

    await offlineDB.saveItem(storeMap[resource], { id: resourceId, ...data })
  }

  async sync(): Promise<{ success: number; failed: number }> {
    if (this.isSyncing || !navigator.onLine) {
      return { success: 0, failed: 0 }
    }

    this.isSyncing = true
    let success = 0
    let failed = 0

    try {
      const queue = await offlineDB.getSyncQueue()

      for (const operation of queue) {
        try {
          await this.syncOperation(operation)
          await offlineDB.removeFromSyncQueue(operation.id)
          success++
        } catch (error) {
          console.error('Sync operation failed:', operation, error)
          failed++
        }
      }

      await this.pullLatestData()

    } catch (error) {
      console.error('Sync failed:', error)
    } finally {
      this.isSyncing = false
    }

    return { success, failed }
  }

  private async syncOperation(operation: {
    id: string
    type: 'create' | 'update' | 'delete'
    resource: string
    resourceId: string
    data: Record<string, unknown>
  }): Promise<void> {
    const { type, resource, resourceId, data } = operation

    switch (type) {
      case 'create':
        await api.post(`/${resource}`, data)
        break
      case 'update':
        await api.patch(`/${resource}/${resourceId}`, data)
        break
      case 'delete':
        await api.delete(`/${resource}/${resourceId}`)
        break
    }
  }

  private async pullLatestData(): Promise<void> {
    try {
      const [tasks, expenses, quotations, mealplans] = await Promise.all([
        api.get<Task[]>('/tasks').catch(() => ({ data: [] })),
        api.get<Expense[]>('/expenses').catch(() => ({ data: [] })),
        api.get<Quotation[]>('/quotations').catch(() => ({ data: [] })),
        api.get<MealPlan[]>('/meal-plans').catch(() => ({ data: [] }))
      ])

      await Promise.all([
        this.updateLocalStore(STORES.TASKS, tasks.data || []),
        this.updateLocalStore(STORES.EXPENSES, expenses.data || []),
        this.updateLocalStore(STORES.QUOTATIONS, quotations.data || []),
        this.updateLocalStore(STORES.MEALPLANS, mealplans.data || [])
      ])

    } catch (error) {
      console.error('Failed to pull latest data:', error)
    }
  }

  private async updateLocalStore<T extends { id: string }>(
    storeName: string,
    items: T[]
  ): Promise<void> {
    await offlineDB.clearStore(storeName)
    
    for (const item of items) {
      await offlineDB.saveItem(storeName, item)
    }
  }

  async getOfflineTasks(): Promise<Task[]> {
    return await offlineDB.getAllItems<Task>(STORES.TASKS)
  }

  async getOfflineExpenses(): Promise<Expense[]> {
    return await offlineDB.getAllItems<Expense>(STORES.EXPENSES)
  }

  async getOfflineQuotations(): Promise<Quotation[]> {
    return await offlineDB.getAllItems<Quotation>(STORES.QUOTATIONS)
  }

  async getOfflineMealPlans(): Promise<MealPlan[]> {
    return await offlineDB.getAllItems<MealPlan>(STORES.MEALPLANS)
  }

  async getPendingOperationsCount(): Promise<number> {
    const queue = await offlineDB.getSyncQueue()
    return queue.length
  }

  isOnline(): boolean {
    return navigator.onLine
  }
}

export const offlineSyncService = new OfflineSyncService()
